<template>
    <div v-if="props.credentialsModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] w-full">
        <div class="bg-white dark:bg-echoes-background-gray rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[120vh] overflow-y-auto">
            <!-- Header Section -->
            <div class="text-center mb-6">
                <!-- Success Icon -->
                <div class="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                </div>
                
                <!-- Title -->
                <h3 class="text-2xl font-bold text-black dark:text-white mb-2">Access Credentials</h3>
                
                <!-- Description -->
                <p class="text-gray-600 dark:text-gray-300 text-sm">
                    Your purchase is confirmed. Please copy your access credentials now, you won't be able to view them again.
                </p>
            </div>

            <!-- Content Section -->
            <div class="space-y-4">
                <!-- Connection Type -->
                <div class="flex items-center gap-2" v-if="props.credentialsModalSale?.listing?.datasets[0]?.external_source">
                    <span class="text-gray-700 dark:text-gray-300 font-medium">Connection:</span>
                    <span class="text-black dark:text-white">{{ props.credentialsModalSale?.listing?.datasets[0]?.external_source?.name }}</span>
                </div>


                <div v-if="props.credentialsModalSale?.credentials[0]?.used">
                    <div class="bg-echoes-primary/10 dark:bg-echoes-primary/20 border border-echoes-primary/20 dark:border-echoes-primary/80 rounded-lg p-4">
                        <p class="text-sm">You have already copied the credentials previously, to obtain them again make another purchase </p>
                    </div>
                </div>
                <!-- Credentials Display -->
                <div v-else-if="showCredentials && confirmedShowCredentials" class="space-y-3">
                    <!-- Credentials in Markdown -->
                    <div class="relative">
                        <div 
                            class="bg-gray-100 dark:bg-echoes-background-black border border-gray-300 dark:border-echoes-border-light rounded-lg p-4 prose prose-sm max-w-none max-h-[200px] overflow-y-auto"
                        >
                            <MarkdownRenderer v-model="datasetCredentials" :is-editing="false" :content-only="true" v-if="datasetCredentials" class="p-4"/>
                            <div v-else>
                                <p class="text-gray-500 dark:text-gray-400 text-sm">You have already copied the credentials previously, to obtain them again make another purchase </p>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="flex gap-2 justify-between">
                        <!-- Copy Button -->
                        <div>
                            <button 
                                class="text-sm flex-1 bg-white dark:bg-echoes-background-gray border border-gray-300 dark:border-echoes-border-light text-gray-700 dark:text-gray-300 rounded-lg px-2 flex items-center justify-center gap-2 hover:bg-gray-50 dark:hover:bg-echoes-border-light transition-colors"
                                @click="copyCredentials"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                                {{ credentialCopied ? 'Copied!' : 'Copy' }}
                            </button>
                        </div>
                            
                        <!-- Save as PDF Button -->
                        <div>
                            <button 
                                class="text-sm flex-1 bg-echoes-primary hover:bg-echoes-primary/80 text-white rounded-lg px-2 flex items-center justify-center gap-2 transition-colors"
                                @click="saveAsPDF"
                                :disabled="isGeneratingPDF"
                            >
                                <svg v-if="!isGeneratingPDF" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                                <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ isGeneratingPDF ? 'Generating...' : 'Save as PDF' }}
                            </button>
                        </div>
                    </div>
                    
                    <!-- Warning Message -->
                    <p class="text-echoes-primary dark:text-echoes-primary text-xs text-center">
                        Copy now—you won't be able to view this again.
                    </p>
                </div>

                <!-- Confirmation Step -->
                <div v-else-if="!showCredentials && confirmedShowCredentials" class="space-y-4">
                    <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <p class="text-yellow-800 dark:text-yellow-200 text-sm text-center">
                            Are you sure you want to view your credentials now? You can only view them once.
                        </p>
                    </div>
                    <div class="flex gap-3">
                        <button 
                            class="flex-1 bg-echoes-primary text-white rounded-lg px-4 py-2 hover:bg-echoes-primary/80 transition-colors"
                            @click="toggleCredentials"
                        >
                            Yes, Get Credentials
                        </button>
                        <button 
                            class="flex-1 bg-gray-200 dark:bg-echoes-background-gray text-gray-700 dark:text-gray-300 rounded-lg px-4 py-2 hover:bg-gray-300 dark:hover:bg-echoes-border-light transition-colors"
                            @click="toggleConfirmedShowCredentials"
                        >
                            No
                        </button>
                    </div>
                </div>

                <!-- Initial Step -->
                <div v-else class="space-y-4">
                    <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <p class="text-blue-800 dark:text-blue-200 text-sm text-center">
                            You can only view the credentials once. Please copy them now for your own records.
                        </p>
                    </div>
                    <button 
                        class="w-full bg-echoes-primary text-white rounded-lg px-4 py-2 hover:bg-echoes-primary/80 transition-colors"
                        @click="toggleConfirmedShowCredentials"
                    >
                        Get Credentials
                    </button>
                </div>
            </div>

            <!-- Footer Section -->
            <div class="flex gap-4 mt-8 justify-between">
                <div>
                    <router-link 
                        class="bg-gray-200 dark:bg-echoes-background-gray text-gray-700 dark:text-gray-300 border-2 border-gray-400 dark:border-echoes-border-light rounded-lg px-4 py-3 hover:bg-gray-300 dark:hover:bg-echoes-border-light transition-all duration-200 text-sm font-medium text-center block"
                        to="/explore"
                        >
                        Explore more Datasets
                    </router-link>
                </div>
                <div>
                    <button 
                        class="bg-transparent dark:bg-transparent text-gray-600 dark:text-gray-400 border-2 border-gray-300 dark:border-echoes-border-light rounded-lg px-4 py-3 hover:bg-gray-50 dark:hover:bg-echoes-border-light/20 transition-all duration-200 text-sm font-medium"
                        @click="closeCredentialsModal"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import html2pdf from 'html2pdf.js';

const { addToast } = useToast();

const props = defineProps({
    credentialsModalOpen: {
        type: Boolean,
        required: true
    },
    credentialsModalSale: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['closeCredentialsModal', 'refreshData']);

const showCredentials = ref(false);
const datasetCredentials = ref(null);
const confirmedShowCredentials = ref(false);
const credentialCopied = ref(false);
const isGeneratingPDF = ref(false);

const toggleConfirmedShowCredentials = () => {
    confirmedShowCredentials.value = !confirmedShowCredentials.value;
}

const toggleCredentials = async () => {
    if(confirmedShowCredentials.value) {
        showCredentials.value = !showCredentials.value;
        if(showCredentials.value) {
            datasetCredentials.value = await getCredentials(props.credentialsModalSale.credentials[0].id);
            emit('refreshData');
        } else {
            datasetCredentials.value = null;
        }
    }
}

const getCredentials = async (credentials_id) => {
    const response = await axios.get(`/api/sale-credentials/${credentials_id}`);
    return response.data;
}

const copyCredentials = () => {
    navigator.clipboard.writeText(datasetCredentials.value);
    credentialCopied.value = true;
    addToast('Credentials copied to clipboard', 'success');
    setTimeout(() => {
        credentialCopied.value = false;
    }, 1000);
}

const saveAsPDF = async () => {
    if (!datasetCredentials.value) {
        addToast('No credentials to save', 'error');
        return;
    }

    isGeneratingPDF.value = true;
    
    try {
        // Create a temporary container for the PDF content
        const tempContainer = document.createElement('div');
        tempContainer.style.padding = '0';
        tempContainer.style.fontFamily = 'Arial, sans-serif';
        tempContainer.style.fontSize = '12px';
        tempContainer.style.lineHeight = '1.5';
        tempContainer.style.color = '#000';
        tempContainer.style.backgroundColor = '#fff'; // White background for the document
        
        // Add logo at the top center with black background
        const logoContainer = document.createElement('div');
        logoContainer.style.textAlign = 'center';
        logoContainer.style.padding = '15px';
        logoContainer.style.backgroundColor = '#000';
        logoContainer.style.marginBottom = '10px';
        logoContainer.style.border = '2px solid #333';
        logoContainer.style.borderRadius = '8px';
        logoContainer.style.width = '100%';
        logoContainer.style.boxSizing = 'border-box';
        logoContainer.innerHTML = `
            <a href="https://datalab.portexai.com" target="_blank" style="text-decoration: none;">
                <img src="/logo-dark.png" alt="PORTEX Logo" style="height: 40px; width: auto;">
            </a>
        `;
        tempContainer.appendChild(logoContainer);
        
        // Create main content container
        const mainContent = document.createElement('div');
        mainContent.style.backgroundColor = '#fff';
        mainContent.style.padding = '20px 30px';
        
        // Add header content (without logo)
        const header = document.createElement('div');
        header.innerHTML = `
            <h1 style="color: #059669; margin-bottom: 15px; font-size: 24px; text-align: center;">Access Credentials</h1>
            <p style="color: #666; margin-bottom: 20px; font-size: 14px; text-align: left;">
                Dataset: ${props.credentialsModalSale.listing?.name || 'Unknown Dataset'}<br>
                Connection: ${props.credentialsModalSale.listing?.datasets?.[0]?.external_source?.name || 'S3 bucket'}<br>
                Generated on: ${new Date().toLocaleDateString()}
            </p>
            <hr style="border: 1px solid #ddd; margin: 20px 0;">
        `;
        mainContent.appendChild(header);
        
        // Create a temporary markdown renderer to get the HTML content
        const markdownContainer = document.createElement('div');
        markdownContainer.className = 'prose prose-sm max-w-none';
        markdownContainer.style.color = '#000';
        
        // Import markdown-it to render the content
        const { default: MarkdownIt } = await import('markdown-it');
        const md = new MarkdownIt();
        markdownContainer.innerHTML = md.render(datasetCredentials.value);
        
        // Wait for all images to load completely
        const images = markdownContainer.querySelectorAll('img');
        if (images.length > 0) {
            await Promise.all(
                Array.from(images).map((img, index) => {
                    console.log(index);
                    return new Promise((resolve) => {
                        
                        // If image is already loaded
                        if (img.complete && img.naturalHeight !== 0) {
                            resolve();
                        } else {
                            // Wait for image to load
                            img.onload = () => {
                                resolve();
                            };
                            img.onerror = () => {
                                resolve(); // Resolve anyway to continue
                            };
                            
                            // Set a timeout as fallback
                            setTimeout(() => {
                                resolve();
                            }, 10000); // 10 second timeout
                        }
                    });
                })
            );
            console.log('All images processed');
        }
        
        mainContent.appendChild(markdownContainer);
        
        // Add footer
        const footer = document.createElement('div');
        footer.innerHTML = `
            <hr style="border: 1px solid #ddd; margin: 20px 0;">
            <p style="color: #666; font-size: 10px; text-align: center;">
                This document contains sensitive credentials. Keep it secure and do not share with unauthorized parties.
            </p>
        `;
        mainContent.appendChild(footer);
        
        tempContainer.appendChild(mainContent);
        
        // Configure PDF options
        const pdfOptions = {
            margin: [10, 10, 10, 10],
            filename: `credentials_${props.credentialsModalSale.listing?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'dataset'}_${new Date().toISOString().split('T')[0]}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { 
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff' // White background
            },
            jsPDF: { 
                unit: 'mm', 
                format: 'a4', 
                orientation: 'portrait' 
            }
        };
        
        // Generate and download PDF
        await html2pdf().from(tempContainer).set(pdfOptions).save();
        
        addToast('PDF saved successfully', 'success');
    } catch (error) {
        console.error('Error generating PDF:', error);
        addToast('Failed to generate PDF', 'error');
    } finally {
        isGeneratingPDF.value = false;
    }
}

const closeCredentialsModal = () => {
    showCredentials.value = false;
    confirmedShowCredentials.value = false;
    datasetCredentials.value = null;
    emit('closeCredentialsModal');
}
</script>

<style scoped lang="postcss">
/* No specific styles needed */
</style>